import { CONSENT_CATEGORIES } from './consent.constants';
import type { ConsentCategoryKey, ConsentService, ConsentState, ServiceConsentState } from './consent.types';

const isRequired = (category: ConsentCategoryKey): boolean =>
    CONSENT_CATEGORIES.some((entry) => entry.key === category && entry.required);

/**
 * Services that need consent. Required ones (technically necessary) are exempt
 * under § 25 Abs. 2 TDDDG: they are never stored and never re-prompted, they
 * only appear in the banner for transparency.
 */
export const consentRelevant = (services: readonly ConsentService[]): ConsentService[] =>
    services.filter((service) => !isRequired(service.category));

export const servicesInCategory = (services: readonly ConsentService[], category: ConsentCategoryKey) =>
    services.filter((service) => service.category === category);

/**
 * Services the visitor has never been asked about. Any hit forces the banner
 * open again: a decision only covers the ids it was recorded with, so a service
 * added afterwards is not consented to (Art. 4 Nr. 11 DSGVO — informed and
 * specific). Removing a service leaves an orphaned entry, which is harmless.
 */
export function findUndecidedServices(
    services: readonly ConsentService[],
    stored: ServiceConsentState,
): ConsentService[] {
    return consentRelevant(services).filter((service) => !(service.id in stored));
}

/** An explicit yes/no for every consent-relevant service — never inherited. */
export function buildServiceConsent(services: readonly ConsentService[], granted: boolean): ServiceConsentState {
    const state: ServiceConsentState = {};
    for (const service of consentRelevant(services)) state[service.id] = granted;

    return state;
}

/**
 * Categories are derived, never stored: a category counts as granted when at
 * least one of its services is. Required categories are always on; a category
 * without services has nothing to grant.
 */
export function deriveCategories(
    services: readonly ConsentService[],
    serviceConsent: ServiceConsentState,
): ConsentState {
    const state = {} as ConsentState;
    for (const category of CONSENT_CATEGORIES) {
        state[category.key] = category.required
            ? true
            : servicesInCategory(services, category.key).some((service) => serviceConsent[service.id] === true);
    }
    return state;
}

/** Whether a single service may run. Only an explicit `true` grants it. */
export function isServiceAllowed(service: ConsentService, serviceConsent: ServiceConsentState): boolean {
    if (isRequired(service.category)) return true;
    return serviceConsent[service.id] === true;
}

/** Same check by id — used by the provider, where only the id is known. */
export function isServiceIdAllowed(
    id: string,
    available: readonly ConsentService[],
    serviceConsent: ServiceConsentState,
): boolean {
    const service = available.find((entry) => entry.id === id);
    return service ? isServiceAllowed(service, serviceConsent) : false;
}

/** The category switch is a master over the services it contains. */
export function selectCategory(
    selection: ServiceConsentState,
    categoryServices: readonly ConsentService[],
    checked: boolean,
): ServiceConsentState {
    const next = { ...selection };
    for (const service of consentRelevant(categoryServices)) next[service.id] = checked;

    return next;
}

/** A single service follows the visitor's click; its category is derived from it. */
export function selectService(
    selection: ServiceConsentState,
    service: ConsentService,
    checked: boolean,
): ServiceConsentState {
    return { ...selection, [service.id]: checked };
}

/** Whether the panel shows a service switch as on. */
export function isServiceSelected(selection: ServiceConsentState, service: ConsentService): boolean {
    if (isRequired(service.category)) return true;
    return selection[service.id] === true;
}

/** Whether the panel shows the category switch as on. */
export function isCategorySelected(
    selection: ServiceConsentState,
    category: ConsentCategoryKey,
    categoryServices: readonly ConsentService[],
): boolean {
    if (isRequired(category)) return true;
    return categoryServices.some((service) => isServiceSelected(selection, service));
}

/** Whether any consent-relevant service was granted — drives the cookie lifetime. */
export const isAcceptingConsent = (serviceConsent: ServiceConsentState): boolean =>
    Object.values(serviceConsent).some((granted) => granted === true);
